import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import toast from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Slider,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  Paper,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
} from '@mui/material';
import {
  Close,
  ZoomIn,
  ZoomOut,
  NavigateBefore,
  NavigateNext,
  FirstPage,
  LastPage,
  Download,
  Fullscreen,
  FullscreenExit,
  Print,
  RotateRight,
  EditNote,
  Save,
  HighlightAlt,
  Brush,
  DeleteOutline,
  PanTool,
} from '@mui/icons-material';

// react-pdf v10+ ships styles under dist/Page (not dist/esm/Page)
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { getAuthToken } from '../../lib/authToken';

// Serve worker from /public — avoids Vite optimizeDeps/?url breakage with pdfjs-dist
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

export type PdfAnnotationType = 'note' | 'highlight' | 'ink';
export type PdfAnnotationTool = 'pan' | PdfAnnotationType;

export interface PdfAnnotation {
  id: string;
  type: PdfAnnotationType;
  page: number;
  /** Sticky note position (% of page) — also accepted as xPct/yPct from API */
  x?: number;
  y?: number;
  xPct?: number;
  yPct?: number;
  text?: string;
  /** Highlight rectangle (% of page) */
  wPct?: number;
  hPct?: number;
  /** Ink stroke points (% of page) */
  points?: { x: number; y: number }[];
  color?: string;
  strokeWidth?: number;
  createdAt?: string;
}

const HIGHLIGHT_COLORS = ['#F6E05E', '#68D391', '#63B3ED', '#FC8181'];
const INK_COLORS = ['#E53E3E', '#2B6CB0', '#2F855A', '#1A202C'];

function normalizeAnnotation(raw: Partial<PdfAnnotation> & Record<string, unknown>): PdfAnnotation | null {
  if (!raw || typeof raw !== 'object') return null;
  const page = Number(raw.page) || 1;
  const id = typeof raw.id === 'string' ? raw.id : crypto.randomUUID();
  let type = raw.type as PdfAnnotationType | undefined;
  if (!type) {
    if (Array.isArray(raw.points) && raw.points.length) type = 'ink';
    else if (raw.wPct != null || raw.w != null) type = 'highlight';
    else type = 'note';
  }

  if (type === 'ink') {
    const points = (Array.isArray(raw.points) ? raw.points : [])
      .filter((p) => p && typeof p === 'object')
      .map((p: any) => ({
        x: Math.min(100, Math.max(0, Number(p.x) || 0)),
        y: Math.min(100, Math.max(0, Number(p.y) || 0)),
      }))
      .slice(0, 800);
    if (points.length < 2) return null;
    return {
      id,
      type: 'ink',
      page,
      points,
      color: typeof raw.color === 'string' ? raw.color : INK_COLORS[0],
      strokeWidth: Math.min(12, Math.max(1, Number(raw.strokeWidth) || 2.5)),
      createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : undefined,
    };
  }

  if (type === 'highlight') {
    const xPct = Math.min(100, Math.max(0, Number(raw.xPct ?? raw.x) || 0));
    const yPct = Math.min(100, Math.max(0, Number(raw.yPct ?? raw.y) || 0));
    const wPct = Math.min(100, Math.max(0.5, Number(raw.wPct ?? raw.w) || 5));
    const hPct = Math.min(100, Math.max(0.5, Number(raw.hPct ?? raw.h) || 2));
    return {
      id,
      type: 'highlight',
      page,
      xPct,
      yPct,
      wPct,
      hPct,
      color: typeof raw.color === 'string' ? raw.color : HIGHLIGHT_COLORS[0],
      createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : undefined,
    };
  }

  const x = Math.min(100, Math.max(0, Number(raw.x ?? raw.xPct) || 0));
  const y = Math.min(100, Math.max(0, Number(raw.y ?? raw.yPct) || 0));
  return {
    id,
    type: 'note',
    page,
    x,
    y,
    xPct: x,
    yPct: y,
    text: String(raw.text || '').slice(0, 2000),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : undefined,
  };
}

interface PDFViewerProps {
  url: string;
  title?: string;
  open: boolean;
  onClose: () => void;
  onDownload?: () => void;
  documentId?: string;
  annotationsEnabled?: boolean;
  initialAnnotations?: PdfAnnotation[];
  existingMetadata?: Record<string, unknown>;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  url,
  title = 'PDF Document',
  open,
  onClose,
  onDownload,
  documentId,
  annotationsEnabled = false,
  initialAnnotations = [],
}) => {
  const theme = useTheme();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [tool, setTool] = useState<PdfAnnotationTool>('pan');
  const [annotations, setAnnotations] = useState<PdfAnnotation[]>([]);
  const [savingAnnotations, setSavingAnnotations] = useState(false);
  const [highlightColor, setHighlightColor] = useState(HIGHLIGHT_COLORS[0]);
  const [inkColor, setInkColor] = useState(INK_COLORS[0]);
  const [draftHighlight, setDraftHighlight] = useState<{
    xPct: number;
    yPct: number;
    wPct: number;
    hPct: number;
  } | null>(null);
  const [draftInk, setDraftInk] = useState<{ x: number; y: number }[] | null>(null);
  const [dirty, setDirty] = useState(false);

  const pageWrapperRef = useRef<HTMLDivElement>(null);
  const drawingRef = useRef<{
    active: boolean;
    startX: number;
    startY: number;
  } | null>(null);

  useEffect(() => {
    if (open) {
      setPageNumber(1);
      setPageInputValue('1');
      setScale(1.0);
      setRotation(0);
      setLoading(true);
      setError(null);
      setTool('pan');
      setDraftHighlight(null);
      setDraftInk(null);
      setDirty(false);
      const normalized = (initialAnnotations || [])
        .map((a) => normalizeAnnotation(a as any))
        .filter(Boolean) as PdfAnnotation[];
      setAnnotations(normalized);
    }
  }, [open, initialAnnotations]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('PDF Load Error:', error);
    setError('Failed to load PDF document. The file may be corrupted or inaccessible.');
    setLoading(false);
  }, []);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= (numPages || 1)) {
      setPageNumber(page);
      setPageInputValue(page.toString());
    }
  };

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInputValue(e.target.value);
  };

  const handlePageInputBlur = () => {
    const page = parseInt(pageInputValue, 10);
    if (!isNaN(page) && page >= 1 && page <= (numPages || 1)) {
      setPageNumber(page);
    } else {
      setPageInputValue(pageNumber.toString());
    }
  };

  const handlePageInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handlePageInputBlur();
    }
  };

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.25, 3.0));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.25, 0.5));
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);
  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  const handlePrint = () => {
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const pctFromEvent = (e: React.MouseEvent | React.PointerEvent) => {
    const el = pageWrapperRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    return {
      x: Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100)),
      y: Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100)),
    };
  };

  const handlePageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (tool !== 'note') return;
    // Ignore if this was end of a drag gesture
    if (drawingRef.current?.active) return;
    const { x, y } = pctFromEvent(e);
    const text = window.prompt('Note text:');
    if (!text?.trim()) return;
    setAnnotations((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'note',
        page: pageNumber,
        x,
        y,
        xPct: x,
        yPct: y,
        text: text.trim(),
        createdAt: new Date().toISOString(),
      },
    ]);
    setDirty(true);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (tool !== 'highlight' && tool !== 'ink') return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const { x, y } = pctFromEvent(e);
    drawingRef.current = { active: true, startX: x, startY: y };
    if (tool === 'highlight') {
      setDraftHighlight({ xPct: x, yPct: y, wPct: 0, hPct: 0 });
    } else {
      setDraftInk([{ x, y }]);
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drawingRef.current?.active) return;
    const { x, y } = pctFromEvent(e);
    if (tool === 'highlight' && drawingRef.current) {
      const xPct = Math.min(drawingRef.current.startX, x);
      const yPct = Math.min(drawingRef.current.startY, y);
      const wPct = Math.abs(x - drawingRef.current.startX);
      const hPct = Math.abs(y - drawingRef.current.startY);
      setDraftHighlight({ xPct, yPct, wPct, hPct });
    } else if (tool === 'ink') {
      setDraftInk((prev) => (prev ? [...prev, { x, y }] : [{ x, y }]));
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drawingRef.current?.active) return;
    const start = drawingRef.current;
    drawingRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }

    const { x, y } = pctFromEvent(e);

    if (tool === 'highlight' && start) {
      const xPct = Math.min(start.startX, x);
      const yPct = Math.min(start.startY, y);
      const wPct = Math.abs(x - start.startX);
      const hPct = Math.abs(y - start.startY);
      setDraftHighlight(null);
      if (wPct >= 0.8 && hPct >= 0.4) {
        setAnnotations((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            type: 'highlight',
            page: pageNumber,
            xPct,
            yPct,
            wPct,
            hPct,
            color: highlightColor,
            createdAt: new Date().toISOString(),
          },
        ]);
        setDirty(true);
      }
      return;
    }

    if (tool === 'ink') {
      setDraftInk((current) => {
        const points = current && current.length >= 2 ? current : null;
        if (points) {
          setAnnotations((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              type: 'ink',
              page: pageNumber,
              points,
              color: inkColor,
              strokeWidth: 2.5,
              createdAt: new Date().toISOString(),
            },
          ]);
          setDirty(true);
        }
        return null;
      });
    }
  };

  const handleDeleteAnnotation = (id: string) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    setDirty(true);
  };

  const handleClearPage = () => {
    if (!window.confirm(`Remove all annotations on page ${pageNumber}?`)) return;
    setAnnotations((prev) => prev.filter((a) => a.page !== pageNumber));
    setDirty(true);
  };

  const handleSaveAnnotations = async () => {
    if (!documentId) return;
    setSavingAnnotations(true);
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      const payload = annotations.map((a) => {
        if (a.type === 'highlight') {
          return {
            id: a.id,
            type: 'highlight',
            page: a.page,
            xPct: a.xPct,
            yPct: a.yPct,
            wPct: a.wPct,
            hPct: a.hPct,
            color: a.color,
            createdAt: a.createdAt,
          };
        }
        if (a.type === 'ink') {
          return {
            id: a.id,
            type: 'ink',
            page: a.page,
            points: a.points,
            color: a.color,
            strokeWidth: a.strokeWidth,
            createdAt: a.createdAt,
          };
        }
        return {
          id: a.id,
          type: 'note',
          page: a.page,
          x: a.x ?? a.xPct,
          y: a.y ?? a.yPct,
          xPct: a.x ?? a.xPct,
          yPct: a.y ?? a.yPct,
          text: a.text,
          createdAt: a.createdAt,
        };
      });

      const response = await fetch(`/api/documents/${documentId}/annotations`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ annotations: payload }),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to save annotations.'));
      }

      toast.success('Annotations saved');
      setDirty(false);
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Failed to save annotations.'));
    } finally {
      setSavingAnnotations(false);
    }
  };

  const pageAnnotations = annotations.filter((a) => a.page === pageNumber);
  const annotateCursor =
    tool === 'note' || tool === 'highlight' || tool === 'ink' ? 'crosshair' : 'default';

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          goToPage(pageNumber - 1);
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          goToPage(pageNumber + 1);
          break;
        case 'Home':
          goToPage(1);
          break;
        case 'End':
          if (numPages) goToPage(numPages);
          break;
        case '+':
        case '=':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleZoomIn();
          }
          break;
        case '-':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleZoomOut();
          }
          break;
        case 'Escape':
          if (draftHighlight || draftInk) {
            setDraftHighlight(null);
            setDraftInk(null);
            drawingRef.current = null;
          } else if (isFullscreen) {
            setIsFullscreen(false);
          } else if (tool !== 'pan') {
            setTool('pan');
          } else {
            onClose();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, pageNumber, numPages, isFullscreen, onClose, tool, draftHighlight, draftInk]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen}
      PaperProps={{
        sx: {
          width: isFullscreen ? '100%' : '90vw',
          height: isFullscreen ? '100%' : '90vh',
          maxWidth: isFullscreen ? '100%' : '1200px',
          maxHeight: isFullscreen ? '100%' : '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 1,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" noWrap sx={{ maxWidth: 240 }}>
            {title}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="First Page">
                <IconButton size="small" onClick={() => goToPage(1)} disabled={pageNumber <= 1}>
                  <FirstPage fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Previous Page">
                <IconButton size="small" onClick={() => goToPage(pageNumber - 1)} disabled={pageNumber <= 1}>
                  <NavigateBefore fontSize="small" />
                </IconButton>
              </Tooltip>

              <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
                <TextField
                  value={pageInputValue}
                  onChange={handlePageInputChange}
                  onBlur={handlePageInputBlur}
                  onKeyPress={handlePageInputKeyPress}
                  size="small"
                  sx={{ width: 50 }}
                  inputProps={{ style: { textAlign: 'center', padding: '4px 8px' } }}
                />
                <Typography variant="body2" sx={{ mx: 1 }}>
                  / {numPages || '?'}
                </Typography>
              </Box>

              <Tooltip title="Next Page">
                <IconButton
                  size="small"
                  onClick={() => goToPage(pageNumber + 1)}
                  disabled={pageNumber >= (numPages || 1)}
                >
                  <NavigateNext fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Last Page">
                <IconButton
                  size="small"
                  onClick={() => numPages && goToPage(numPages)}
                  disabled={pageNumber >= (numPages || 1)}
                >
                  <LastPage fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Zoom Out">
                <IconButton size="small" onClick={handleZoomOut} disabled={scale <= 0.5}>
                  <ZoomOut fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
                {Math.round(scale * 100)}%
              </Typography>
              <Tooltip title="Zoom In">
                <IconButton size="small" onClick={handleZoomIn} disabled={scale >= 3.0}>
                  <ZoomIn fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {annotationsEnabled && (
              <>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={tool}
                  onChange={(_, v) => v && setTool(v)}
                >
                  <ToggleButton value="pan" aria-label="pan">
                    <Tooltip title="Navigate">
                      <PanTool fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="note" aria-label="sticky note">
                    <Tooltip title="Sticky note">
                      <EditNote fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="highlight" aria-label="highlight">
                    <Tooltip title="Highlight (drag)">
                      <HighlightAlt fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="ink" aria-label="ink">
                    <Tooltip title="Ink pen (draw)">
                      <Brush fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>

                {tool === 'highlight' && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {HIGHLIGHT_COLORS.map((c) => (
                      <Box
                        key={c}
                        onClick={() => setHighlightColor(c)}
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: 0.5,
                          bgcolor: c,
                          cursor: 'pointer',
                          border: highlightColor === c ? '2px solid' : '1px solid',
                          borderColor: highlightColor === c ? 'primary.main' : 'divider',
                        }}
                      />
                    ))}
                  </Stack>
                )}
                {tool === 'ink' && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {INK_COLORS.map((c) => (
                      <Box
                        key={c}
                        onClick={() => setInkColor(c)}
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: c,
                          cursor: 'pointer',
                          border: inkColor === c ? '2px solid' : '1px solid',
                          borderColor: inkColor === c ? 'primary.main' : 'divider',
                        }}
                      />
                    ))}
                  </Stack>
                )}

                <Tooltip title="Clear annotations on this page">
                  <IconButton size="small" onClick={handleClearPage} disabled={pageAnnotations.length === 0}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title={dirty ? 'Save annotations (unsaved changes)' : 'Save annotations'}>
                  <span>
                    <IconButton
                      size="small"
                      color={dirty ? 'primary' : 'default'}
                      onClick={handleSaveAnnotations}
                      disabled={!documentId || savingAnnotations || annotations.length === 0}
                    >
                      <Save fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {dirty && <Chip size="small" label="Unsaved" color="warning" />}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              </>
            )}

            <Tooltip title="Rotate">
              <IconButton size="small" onClick={handleRotate}>
                <RotateRight fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton size="small" onClick={handlePrint}>
                <Print fontSize="small" />
              </IconButton>
            </Tooltip>
            {onDownload && (
              <Tooltip title="Download">
                <IconButton size="small" onClick={onDownload}>
                  <Download fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              <IconButton size="small" onClick={toggleFullscreen}>
                {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" onClick={onClose}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200',
          overflow: 'auto',
        }}
      >
        {loading && !error && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading PDF...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ maxWidth: 500 }}>
              {error}
            </Alert>
          </Box>
        )}

        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={null}
          error={null}
        >
          <Paper
            elevation={4}
            sx={{
              m: 2,
              display: loading || error ? 'none' : 'block',
            }}
          >
            <Box
              ref={pageWrapperRef}
              sx={{
                position: 'relative',
                display: 'inline-block',
                cursor: annotateCursor,
                touchAction: tool === 'ink' || tool === 'highlight' ? 'none' : 'auto',
                userSelect: tool === 'pan' ? 'auto' : 'none',
              }}
              onClick={handlePageClick}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={() => {
                drawingRef.current = null;
                setDraftHighlight(null);
                setDraftInk(null);
              }}
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                rotate={rotation}
                renderTextLayer={tool === 'pan'}
                renderAnnotationLayer={tool === 'pan'}
                loading={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 800,
                      width: 600,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                }
              />

              {/* SVG overlay for highlights + ink */}
              <Box
                component="svg"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 5,
                }}
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                {pageAnnotations
                  .filter((a) => a.type === 'highlight')
                  .map((a) => (
                    <rect
                      key={a.id}
                      x={a.xPct}
                      y={a.yPct}
                      width={a.wPct}
                      height={a.hPct}
                      fill={a.color || HIGHLIGHT_COLORS[0]}
                      opacity={0.45}
                      style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                      onDoubleClick={(ev) => {
                        ev.stopPropagation();
                        handleDeleteAnnotation(a.id);
                      }}
                    >
                      <title>Double-click to delete highlight</title>
                    </rect>
                  ))}
                {draftHighlight && (
                  <rect
                    x={draftHighlight.xPct}
                    y={draftHighlight.yPct}
                    width={draftHighlight.wPct}
                    height={draftHighlight.hPct}
                    fill={highlightColor}
                    opacity={0.35}
                    stroke={highlightColor}
                    strokeWidth={0.15}
                  />
                )}
                {pageAnnotations
                  .filter((a) => a.type === 'ink' && a.points && a.points.length > 1)
                  .map((a) => (
                    <polyline
                      key={a.id}
                      fill="none"
                      stroke={a.color || INK_COLORS[0]}
                      strokeWidth={(a.strokeWidth || 2.5) * 0.15}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      points={a.points!.map((p) => `${p.x},${p.y}`).join(' ')}
                      style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
                      onDoubleClick={(ev) => {
                        ev.stopPropagation();
                        handleDeleteAnnotation(a.id);
                      }}
                    >
                      <title>Double-click to delete ink</title>
                    </polyline>
                  ))}
                {draftInk && draftInk.length > 1 && (
                  <polyline
                    fill="none"
                    stroke={inkColor}
                    strokeWidth={0.35}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points={draftInk.map((p) => `${p.x},${p.y}`).join(' ')}
                  />
                )}
              </Box>

              {pageAnnotations
                .filter((a) => a.type === 'note')
                .map((note) => (
                  <Paper
                    key={note.id}
                    elevation={3}
                    onDoubleClick={(ev) => {
                      ev.stopPropagation();
                      handleDeleteAnnotation(note.id);
                    }}
                    title="Double-click to delete note"
                    sx={{
                      position: 'absolute',
                      left: `${note.x ?? note.xPct}%`,
                      top: `${note.y ?? note.yPct}%`,
                      transform: 'translate(-50%, -50%)',
                      maxWidth: 180,
                      p: 0.75,
                      bgcolor: 'warning.light',
                      fontSize: 12,
                      zIndex: 10,
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                    }}
                  >
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.3 }}>
                      {note.text}
                    </Typography>
                  </Paper>
                ))}
            </Box>
          </Paper>
        </Document>
      </DialogContent>

      {numPages && numPages > 1 && !loading && !error && (
        <DialogActions
          sx={{
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            p: 2,
          }}
        >
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="caption" sx={{ minWidth: 60 }}>
              Page {pageNumber}
            </Typography>
            <Slider
              value={pageNumber}
              min={1}
              max={numPages}
              step={1}
              onChange={(_, value) => goToPage(value as number)}
              valueLabelDisplay="auto"
              sx={{ flex: 1 }}
            />
            <Typography variant="caption" sx={{ minWidth: 60, textAlign: 'right' }}>
              of {numPages}
            </Typography>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PDFViewer;
